import { createContext, useContext, useEffect, useMemo, useRef } from 'react';
import {
  useDocumentUpload,
  IDocumentType,
  IDocumentTypeOption,
} from 'Hooks/useDocumentUpload';
import { useFileUploadHandlers } from '../../Modules/UniversalRequest/FileUpload/DocumentUploadManager/Hooks/useFileUploadHandlers';

export interface IDocumentUploadContextValue {
  availableDocumentTypes: IDocumentTypeOption[];
  documentTypes: IDocumentType[];
  availableTypes: IDocumentTypeOption[];
  hasSelectedTypes: boolean;
  showDeleteButton: boolean;
  addDocumentType: (type: IDocumentTypeOption) => void;
  toggleCollapse: (typeId: string) => void;
  handleDelete: (typeId: string) => void;
  handleFileSelect: (typeId: string, files: FileList) => void;
  handleDeleteFile: (typeId: string, fileId: string) => void;
  handlePreviewFile: (typeId: string, fileId: string) => void;
  showModal: boolean;
  toastQueue: { id: string; message: string }[];
  pendingDeleteType: IDocumentType | null;
  showPreviewModal: boolean;
  previewFile: {
    fileName: string;
    base64: string;
    fileExtension: string;
  } | null;
  handleUndoToast: (toastId: string) => void;
  handleCloseToast: (toastId: string) => void;
  handleConfirmDelete: () => void;
  handleCancelDelete: () => void;
  handleClosePreview: () => void;
  fileValidationErrorByTypeId: Record<string, string | null>;
}

const DocumentUploadContext = createContext<IDocumentUploadContextValue | null>(
  null,
);

export const useDocumentUploadContext = () => {
  const ctx = useContext(DocumentUploadContext);
  if (!ctx) {
    throw new Error(
      'useDocumentUploadContext must be used within DocumentUploadProvider',
    );
  }
  return ctx;
};

interface IDocumentUploadProviderProps {
  availableDocumentTypes: IDocumentTypeOption[];
  onUploadComplete?: (documentTypes: any[]) => void;
  children: React.ReactNode;
}

export const DocumentUploadProvider = ({
  availableDocumentTypes,
  onUploadComplete,
  children,
}: IDocumentUploadProviderProps) => {
  const {
    documentTypes,
    addDocumentType,
    addDocumentTypeWithFiles,
    removeDocumentType,
    toggleCollapse,
    addFile,
    removeFile,
    updateFileProgress,
    updateFileStatus,
    getAvailableTypes,
  } = useDocumentUpload([], onUploadComplete);

  const handlers = useFileUploadHandlers({
    documentTypes,
    addDocumentType,
    addDocumentTypeWithFiles,
    removeDocumentType,
    addFile,
    removeFile,
    updateFileProgress,
    updateFileStatus,
  });

  const availableTypes = useMemo(
    () => getAvailableTypes(availableDocumentTypes),
    [getAvailableTypes, availableDocumentTypes, documentTypes],
  );

  const hasSelectedTypes = documentTypes.length > 0;
  const showDeleteButton = availableDocumentTypes.length > 1;

  const hasAutoAdded = useRef(false);
  useEffect(() => {
    if (
      !hasAutoAdded.current &&
      availableDocumentTypes.length === 1 &&
      availableTypes.length === 1
    ) {
      hasAutoAdded.current = true;
      addDocumentType(availableTypes[0]);
    }
  }, [availableDocumentTypes.length, availableTypes, addDocumentType]);

  const value: IDocumentUploadContextValue = {
    availableDocumentTypes,
    documentTypes,
    availableTypes,
    hasSelectedTypes,
    showDeleteButton,
    addDocumentType,
    toggleCollapse,
    handleDelete: handlers.handleDelete,
    handleFileSelect: handlers.handleFileSelect,
    handleDeleteFile: handlers.handleDeleteFile,
    handlePreviewFile: handlers.handlePreviewFile,
    showModal: handlers.showModal,
    toastQueue: handlers.toastQueue.map((t) => ({
      id: t.id,
      message: t.message,
    })),
    pendingDeleteType: handlers.pendingDeleteType,
    showPreviewModal: handlers.showPreviewModal,
    previewFile: handlers.previewFile,
    handleUndoToast: handlers.handleUndoToast,
    handleCloseToast: handlers.handleCloseToast,
    handleConfirmDelete: handlers.handleConfirmDelete,
    handleCancelDelete: handlers.handleCancelDelete,
    handleClosePreview: handlers.handleClosePreview,
    fileValidationErrorByTypeId: handlers.fileValidationErrorByTypeId,
  };

  return (
    <DocumentUploadContext.Provider value={value}>
      {children}
    </DocumentUploadContext.Provider>
  );
};
